import React, { useEffect, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import ContextHelp from '../../ContextHelp/ContextHelp';
import './RFBFTool.scss';

interface Props {
  map: mapboxgl.Map;
  onClose: () => void;
}

const DAYS = [1, 2, 3] as const;
type Day = typeof DAYS[number];

// EM2 parity: by 13:30 NWT (America/Yellowknife), today's actual peak FWI is
// in — showing the now-stale morning forecast for Day 1 is misleading. Swap
// to live spatial_danger (intellifire:pg_spatial_danger via WFS) instead.
const DAY1_ACTUAL_AFTER_HOUR = 13;
const DAY1_ACTUAL_AFTER_MINUTE = 30;
const NWT_TZ = 'America/Yellowknife';

function nwtAfter1330(now = new Date()): boolean {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: NWT_TZ, hour12: false, hour: '2-digit', minute: '2-digit',
  }).formatToParts(now);
  const hour = Number(parts.find((p) => p.type === 'hour')?.value ?? 0);
  const minute = Number(parts.find((p) => p.type === 'minute')?.value ?? 0);
  return hour > DAY1_ACTUAL_AFTER_HOUR
      || (hour === DAY1_ACTUAL_AFTER_HOUR && minute >= DAY1_ACTUAL_AFTER_MINUTE);
}

const WFS_SPATIAL_DANGER_URL =
  '/api/geoserver/geoserver/intellifire/ows?service=WFS&version=2.0.0' +
  '&request=GetFeature&typeNames=intellifire:pg_spatial_danger' +
  '&outputFormat=application/json';

const SOURCE_ID = (d: Day) => `rfbf-day-${d}`;
const CIRCLE_LAYER_ID = (d: Day) => `rfbf-day-${d}-circle`;
const LABEL_LAYER_ID = (d: Day) => `rfbf-day-${d}-label`;

// 5-class FWI palette — derived from the shared fwiClassification module
// (which loads from /api/fire-danger-classes on app boot, falls back to
// FWI2025 defaults). Built per addLayer call so re-mounts after a backend
// refresh see the latest values.
import { FWI_CLASSIFICATIONS, getFWIDangerClass } from '../../../utils/fwiClassification';
function buildFwiColorExpr(): mapboxgl.Expression {
  const classes = FWI_CLASSIFICATIONS();
  const stops: Array<number | string> = [];
  classes.forEach(c => {
    stops.push(c.minFWI, c.color);
  });
  return [
    'step',
    ['to-number', ['coalesce', ['get', 'fwi'], -1]],
    '#888888',  // missing / null (fwi < 0)
    ...stops,
  ] as mapboxgl.Expression;
}

function lowClassMaxFwi(): number {
  const low = FWI_CLASSIFICATIONS()[0];
  return low.maxFWI ?? low.minFWI;
}

// Properties come in two shapes depending on the source layer:
//   - WFS pg_spatial_danger (Day 1 actual): station_name, wind_speed, temperature, rain_24h, timestamp_str, ...
//   - RFBF /api/rfbf/geojson/:day (Day 1/2/3 forecast): name, ws, temp, precip, hourly + local_date, ...
function pick(p: any, ...keys: string[]): any {
  for (const k of keys) {
    if (p && p[k] !== undefined && p[k] !== null && p[k] !== '') return p[k];
  }
  return null;
}

function fwiRank(fwi: number | null): { name: string; color: string } {
  if (fwi === null || Number.isNaN(fwi)) return { name: 'Unknown', color: '#888' };
  // FWI is non-null thanks to the guard above.
  const klass = getFWIDangerClass(fwi)!;
  return { name: klass.label, color: klass.color };
}

function fmt(n: any, digits = 1): string {
  if (n === null || n === undefined || n === '') return '—';
  const v = Number(n);
  return Number.isFinite(v) ? v.toFixed(digits) : String(n);
}

// Floor-to-N-decimals for the displayed FWI. Default toFixed rounds — a raw
// value of 29.97 then renders as "30.0" and looks like it should be Extreme
// while the classifier (correctly) sees it as Very High. Flooring keeps the
// displayed number on the same side of every band boundary as the raw value.
function fmtFloor(n: any, digits = 1): string {
  if (n === null || n === undefined || n === '') return '—';
  const v = Number(n);
  if (!Number.isFinite(v)) return String(n);
  const factor = Math.pow(10, digits);
  return (Math.floor(v * factor) / factor).toFixed(digits);
}

function buildPopupHTML(props: any, dayLabel: string): string {
  const fwi = props.fwi !== undefined && props.fwi !== null ? Number(props.fwi) : null;
  const rank = fwiRank(fwi);
  const name = pick(props, 'name', 'station_name') || 'Station';
  const date = pick(props, 'local_date', 'hourly', 'timestamp_str') || '';
  const ws = pick(props, 'ws', 'wind_speed');
  const wd = pick(props, 'wd', 'wind_direction');
  const temp = pick(props, 'temp', 'temperature');
  const rh = pick(props, 'rh');
  const precip = pick(props, 'precip', 'rain_24h');
  return `
    <div class="rfbf-popup">
      <div class="rfbf-popup__title">${name}</div>
      <div class="rfbf-popup__sub">${dayLabel}${date ? ` &middot; ${String(date).split('T')[0]}` : ''}</div>
      <div class="rfbf-popup__rank" style="background:${rank.color};">${rank.name} &nbsp; FWI ${fmtFloor(fwi, 1)}</div>
      <table class="rfbf-popup__table">
        <tr><th>FFMC</th><td>${fmt(props.ffmc, 1)}</td><th>DMC</th><td>${fmt(props.dmc, 1)}</td></tr>
        <tr><th>DC</th><td>${fmt(props.dc, 1)}</td><th>BUI</th><td>${fmt(props.bui, 1)}</td></tr>
        <tr><th>ISI</th><td>${fmt(props.isi, 1)}</td><th>FT</th><td>${pick(props, 'ft') ?? '—'}</td></tr>
        <tr><th>Temp</th><td>${fmt(temp, 1)}&deg;C</td><th>RH</th><td>${fmt(rh, 0)}%</td></tr>
        <tr><th>Wind</th><td colspan="3">${fmt(ws, 0)} km/h @ ${fmt(wd, 0)}&deg;</td></tr>
        <tr><th>Precip 24h</th><td colspan="3">${fmt(precip, 1)} mm</td></tr>
      </table>
    </div>
  `;
}

function buildTooltipHTML(props: any): string {
  const fwi = props.fwi !== undefined && props.fwi !== null ? Number(props.fwi) : null;
  const rank = fwiRank(fwi);
  const name = pick(props, 'name', 'station_name') || 'Station';
  return `<div class="rfbf-tip"><strong>${name}</strong><br/>${rank.name} &nbsp; FWI ${fmtFloor(fwi, 1)}</div>`;
}

const RFBFTool: React.FC<Props> = ({ map, onClose }) => {
  const [activeDay, setActiveDay] = useState<Day>(1);
  const [playing, setPlaying] = useState(false);
  const [status, setStatus] = useState<string>('Loading...');
  // Recomputed once on mount — if the tool is left open across 13:30 NWT
  // the user can close + reopen to pick up the actual layer.
  const [day1Actual] = useState<boolean>(() => nwtAfter1330());
  const timerRef = useRef<number | null>(null);

  const dayLabel = (d: Day): string => {
    if (d === 1) return day1Actual ? "Today's Actual" : "Today's Forecast";
    if (d === 2) return "Tomorrow's Forecast";
    return 'Day 3 Forecast';
  };

  // Add sources + layers on mount; remove on unmount
  useEffect(() => {
    let cancelled = false;

    const ensureLayers = () => {
      DAYS.forEach((d) => {
        if (!map.getSource(SOURCE_ID(d))) {
          map.addSource(SOURCE_ID(d), {
            type: 'geojson',
            data: { type: 'FeatureCollection', features: [] },
          });
        }
        if (!map.getLayer(CIRCLE_LAYER_ID(d))) {
          map.addLayer({
            id: CIRCLE_LAYER_ID(d),
            type: 'circle',
            source: SOURCE_ID(d),
            layout: { visibility: d === 1 ? 'visible' : 'none' },
            paint: {
              'circle-radius': 14,
              'circle-color': buildFwiColorExpr(),
              'circle-stroke-color': '#000',
              'circle-stroke-width': 0.5,
              'circle-opacity': 0.9,
            },
          });
        }
        if (!map.getLayer(LABEL_LAYER_ID(d))) {
          map.addLayer({
            id: LABEL_LAYER_ID(d),
            type: 'symbol',
            source: SOURCE_ID(d),
            layout: {
              visibility: d === 1 ? 'visible' : 'none',
              'text-field': [
                'concat',
                String(d),
                '-',
                ['to-string', ['floor', ['to-number', ['coalesce', ['get', 'fwi'], 0]]]],
              ],
              'text-size': 10,
              'text-anchor': 'center',
              'text-allow-overlap': true,
              'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
            },
            paint: {
              'text-color': [
                'case',
                ['<', ['to-number', ['coalesce', ['get', 'fwi'], 0]], lowClassMaxFwi()],
                '#FFFFFF',
                '#000000',
              ],
            },
          });
        }
      });
    };

    const loadData = async () => {
      try {
        const day1Url = day1Actual
          ? WFS_SPATIAL_DANGER_URL
          : '/api/rfbf/geojson/1';
        const urls = [day1Url, '/api/rfbf/geojson/2', '/api/rfbf/geojson/3'];
        const results = await Promise.all(
          urls.map((url, i) =>
            fetch(url, { credentials: 'include' }).then((r) => {
              if (!r.ok) throw new Error(`day ${i + 1}: HTTP ${r.status}`);
              return r.json();
            })
          )
        );
        if (cancelled) return;
        results.forEach((fc, i) => {
          const d = DAYS[i];
          const src = map.getSource(SOURCE_ID(d)) as mapboxgl.GeoJSONSource | undefined;
          if (src) src.setData(fc);
        });
        const total = results.reduce((n, fc) => n + (fc.features?.length || 0), 0);
        const flavour = day1Actual ? 'Day 1 actual + Days 2/3 forecast' : '3-day forecast';
        setStatus(total > 0 ? `${flavour}` : 'No data');
      } catch (err: any) {
        if (!cancelled) setStatus(`Error: ${err.message}`);
      }
    };

    ensureLayers();
    loadData();

    // Hover (tooltip) + click (popup) handlers, scoped to the RFBF circle layers.
    const tooltip = new mapboxgl.Popup({
      closeButton: false, closeOnClick: false, offset: 12, className: 'rfbf-tooltip-popup',
    });
    const popup = new mapboxgl.Popup({
      closeButton: true, closeOnClick: true, offset: 12, className: 'rfbf-detail-popup', maxWidth: '320px',
    });

    const handlersByDay: Array<{ d: Day; layer: string; enter: any; move: any; leave: any; click: any }> = [];

    DAYS.forEach((d) => {
      const layer = CIRCLE_LAYER_ID(d);
      const enter = () => { map.getCanvas().style.cursor = 'pointer'; };
      const move = (e: mapboxgl.MapLayerMouseEvent) => {
        const f = e.features?.[0];
        if (!f) return;
        tooltip.setLngLat(e.lngLat).setHTML(buildTooltipHTML(f.properties || {})).addTo(map);
      };
      const leave = () => { map.getCanvas().style.cursor = ''; tooltip.remove(); };
      const click = (e: mapboxgl.MapLayerMouseEvent) => {
        const f = e.features?.[0];
        if (!f) return;
        const label = dayLabel(d);
        popup.setLngLat(e.lngLat).setHTML(buildPopupHTML(f.properties || {}, `Day ${d} — ${label}`)).addTo(map);
        // Suppress the lingering tooltip so it doesn't overlap the popup.
        tooltip.remove();
      };
      map.on('mouseenter', layer, enter);
      map.on('mousemove', layer, move);
      map.on('mouseleave', layer, leave);
      map.on('click', layer, click);
      handlersByDay.push({ d, layer, enter, move, leave, click });
    });

    return () => {
      cancelled = true;
      if (timerRef.current) window.clearInterval(timerRef.current);
      handlersByDay.forEach(({ layer, enter, move, leave, click }) => {
        map.off('mouseenter', layer, enter);
        map.off('mousemove', layer, move);
        map.off('mouseleave', layer, leave);
        map.off('click', layer, click);
      });
      tooltip.remove();
      popup.remove();
      DAYS.forEach((d) => {
        if (map.getLayer(LABEL_LAYER_ID(d))) map.removeLayer(LABEL_LAYER_ID(d));
        if (map.getLayer(CIRCLE_LAYER_ID(d))) map.removeLayer(CIRCLE_LAYER_ID(d));
        if (map.getSource(SOURCE_ID(d))) map.removeSource(SOURCE_ID(d));
      });
    };
  }, [map, day1Actual]);

  // Switch visible day
  useEffect(() => {
    DAYS.forEach((d) => {
      const vis = d === activeDay ? 'visible' : 'none';
      if (map.getLayer(CIRCLE_LAYER_ID(d))) {
        map.setLayoutProperty(CIRCLE_LAYER_ID(d), 'visibility', vis);
      }
      if (map.getLayer(LABEL_LAYER_ID(d))) {
        map.setLayoutProperty(LABEL_LAYER_ID(d), 'visibility', vis);
      }
    });
  }, [activeDay, map]);

  const start = () => {
    if (timerRef.current) return;
    setPlaying(true);
    timerRef.current = window.setInterval(() => {
      setActiveDay((d) => ((d % 3) + 1) as Day);
    }, 3000);
  };

  const stop = () => {
    if (timerRef.current) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setPlaying(false);
  };

  const reset = () => {
    stop();
    setActiveDay(1);
  };

  return (
    <div className="rfbf-tool">
      <div className="rfbf-tool__header">
        <i className="fa-solid fa-circle-exclamation" /> RFBF Tool
        <span className="rfbf-tool__help">
          <ContextHelp context="rfbf-help" title="RFBF Tool Help" />
        </span>
        <button className="rfbf-tool__close" onClick={onClose} aria-label="Close">×</button>
      </div>
      <div className="rfbf-tool__body">
        <div className="rfbf-tool__current">
          <strong>Day {activeDay}</strong> — {dayLabel(activeDay)}
        </div>
        <div className="rfbf-tool__status">{status}</div>
        <div className="rfbf-tool__days">
          {DAYS.map((d) => (
            <button
              key={d}
              className={`rfbf-tool__day ${d === activeDay ? 'is-active' : ''}`}
              onClick={() => { stop(); setActiveDay(d); }}
            >
              Day {d}
            </button>
          ))}
        </div>
        <div className="rfbf-tool__controls">
          {!playing && (
            <button onClick={start} title="Play"><i className="fa-solid fa-play" /></button>
          )}
          {playing && (
            <button onClick={stop} title="Pause"><i className="fa-solid fa-pause" /></button>
          )}
          <button onClick={reset} title="Reset"><i className="fa-solid fa-rotate-left" /></button>
        </div>
      </div>
    </div>
  );
};

export default RFBFTool;
